<template>
  <div>
    <Card
      :title="'Ads Settings'"
    >
      <template v-slot:button>
        <Button v-if="isEdit" @clicked="disableEdit" variant="default" label="Cancel" />
        <Button v-if="!isEdit" @clicked="setEdit" variant="primary" label="Edit" />
        <Button v-if="isEdit" @clicked="save" variant="primary" label="Save" />
      </template>
      <template v-slot:body>
        <div class="flex flex-col">
          <h5 class="text-gray-700 text-sm mt-3 mb-6 font-bold uppercase text-left">
            Ads Form
          </h5>
          <form-item label='Number of Songs'>
            <template v-slot>
              <Input
                v-model="numberOfSongs"
                :disabled="!isEdit"
                placeholder="Number of Songs"
                :error="($v.numberOfSongs.$dirty && !$v.numberOfSongs.required) || ($v.numberOfSongs.$dirty && !$v.numberOfSongs.integer) || ($v.numberOfSongs.$dirty && !$v.numberOfSongs.minValue)"
              />
              <div class="flex flex-col pt-2">
                <text-error
                  v-if="$v.numberOfSongs.$dirty && !$v.numberOfSongs.required"
                  text="Please fill Number Of Songs."
                >
                </text-error>
                <text-error
                  v-if="$v.numberOfSongs.$dirty && !$v.numberOfSongs.integer"
                  text="Value must be an integer."
                >
                </text-error>
                <text-error
                  v-if="$v.numberOfSongs.$dirty && !$v.numberOfSongs.minValue"
                  text="Value must be greater than zero."
                >
                </text-error>
              </div>
            </template>
          </form-item>
        </div>
        <div class="flex flex-col border-b border-gray-200 py-3">
          <div class="flex justify-between">
            <h5 class="text-gray-700 text-sm py-2 font-bold uppercase">
              Ads List
            </h5>
            <Button v-if="isEdit" variant="outline-primary" label="Add Ad">
              <template v-slot:any>
                <input style="width: 100%;height: 100%;position: absolute;left: 0;top: 0;opacity: 0;cursor: pointer;z-index: 2;"
                       type="file"
                       name="fileAds"
                       @change="onFileMusicChange"
                />
              </template>
            </Button>
          </div>
          <text-error v-if="isEmptyFiles && isEdit" text="Files cannot be empty" />
        </div>
        <el-table :data="files" row-key="id" :show-header="false" style="width: 100%">
          <el-table-column label="Song" width="50px" >
            <template slot-scope="scope">
              <span>{{scope.row.no}}</span>
            </template>
          </el-table-column>
          <el-table-column label="Song" width="auto" >
            <template slot-scope="scope">
              <span>{{scope.row.filename}}</span>
            </template>
          </el-table-column>
          <el-table-column label="Song" width="250px" >
            <template slot-scope="scope">
              <audio controls :src="scope.row.file" :id="'audioPlayer'+scope.row.id" class="audio-player" style="width: 240px;" controlsList="nodownload" type="audio/mp3" preload="none" >
                Audio tag not supported
              </audio>
            </template>
          </el-table-column>
          <el-table-column label="Actions" align="right">
            <template slot-scope="scope">
              <div class="static inline-block text-left container-action">
                <VueCustomTooltip label="Delete" :position="'is-left'" v-if="isEdit">
                  <button @click="removeFile(scope.row.id)" class="p-2 mr-2 rounded bg-red-500 hover:bg-red-600 focus:outline-none" >
                    <font-awesome-icon class="text-white" :icon="['fa', 'trash-alt']" />
                  </button>
                </VueCustomTooltip>
              </div>
            </template>
          </el-table-column>
        </el-table>
        <div class="flex justify-center py-6" v-if="files.length === 0">
          No ads added. Please add an ad to continue
        </div>
      </template>
    </Card>
  </div>
</template>

<script>
import Card from '@/components/Layout/Card';
import { mapActions, mapGetters } from 'vuex';
import { validationMixin } from 'vuelidate';
import { required, integer } from 'vuelidate/lib/validators';
import axios from 'axios';
import { FormItem, Input, TextError, Button } from '@/components/Layout/Form';

const greaterThanZero = value => value > 0;
const API_URL = process.env.API_URL;
export default {
  name: 'AdsSettings',
  mixins: [validationMixin],
  data: () => ({
    numberOfSongs: 0,
    files: [],
    isError: false,
    isEdit: false,
    deletedFiles: [],
  }),
  validations: {
    numberOfSongs: {
      required,
      integer,
      minValue: greaterThanZero,
    },
  },
  computed: {
    ...mapGetters('NotificationModule', ['getErrorCode', 'getMessage', 'getStatus']),
    isEmptyFiles() {
      return this.files.length === 0;
    },
  },
  watch: {
    getPercentageUpload: {
      immediate: true,
      handler(val) {
        if (val !== 0) {
          this.progressbar = val;
        }
      },
    },
  },
  async mounted() {
    this.audioEl = document.createElement('audio');
    await this.loadData();
  },
  methods: {
    ...mapActions('AppSettingModule', ['requestAdsSettings', 'requestSaveAdsSettings', 'requestAddAdsFileTemp']),
    ...mapActions('NotificationModule', [
      'setLoadingWithMessage',
      'unsetLoading',
    ]),
    async loadData() {
      const res = await this.requestAdsSettings();
      if (res) {
        this.numberOfSongs = Number(res.number_of_track);
        this.files = await Promise.all(res.files.map(async (f, index) => {
          const file = await this.getMusicFile(f.mp3_url);
          return Object.assign(
            {},
            {
              no: index + 1,
              id: index,
              file,
              filename: f.file_name,
              key: f.file,
            },
          );
        }));
      }
    },
    async save() {
      try {
        this.$v.$touch();
        if (!this.$v.$invalid && !this.isEmptyFiles) {
          const files = Object.assign([], this.files.map(f => f.key));
          const deletedFiles = Object.assign([], this.deletedFiles.map(f => f));
          const dataAds = {
            number_of_track: this.numberOfSongs,
            files,
            files_delete: deletedFiles,
          };
          await this.requestSaveAdsSettings(dataAds);
          await this.resetState();
          await this.loadData();
          if (this.getStatus === 200) {
            this.$swal('Ads Settings Saved', '', 'success');
            this.loadData();
          } else if (this.getStatus === 400 && this.getErrorCode === 'error-save-ads-settings') {
            this.$swal('Error', this.getMessage, 'error');
          }
        }
      } catch (err) {
        // eslint-disable-next-line no-console
        console.log(err);
        this.isError = true;
        this.$swal('Error', err.data.message, 'error');
      }
    },
    async resetState() {
      this.$v.$reset();
      this.isError = false;
      this.deletedFiles = [];
    },
    async onFileMusicChange(e) {
      const fileTypeAllowed = ['audio/mp3', 'audio/mpeg', 'audio/MPA', 'audio/mpa-robust'];
      const file = e.target.files[0];
      if (!fileTypeAllowed.includes(file.type)) {
        this.$swal('Error', 'File format must be mp3', 'error');
      } else {
        try {
          const fileTemp = await this.uploadFile(file);
          const data = {
            no: 0,
            id: 0,
            file: URL.createObjectURL(file),
            filename: file.name,
            key: fileTemp.data.key,
          };
          this.files.push(data);
          this.reOrderFiles();
        } catch (err) {
          console.log(err.message);
          this.$swal('Error', err.data.message, 'error');
        }
      }
    },
    async getMusicFile(file) {
      const hostname = file.indexOf('//') > -1 ? `https://${file.split('/')[2]}` : file.split('/')[0];
      const fileAudio = await axios({
        method: 'get',
        url: file.replace(hostname, API_URL),
      });
      return fileAudio.data;
    },
    async uploadFile(file) {
      this.setLoadingWithMessage('uploading file : 0%');
      const dataSong = new FormData();
      dataSong.append('file', file);
      return await this.requestAddAdsFileTemp({
        file: dataSong,
        callback: (event) => {
          const percentCompleted = Math.round((100 * event.loaded) / event.total);
          this.setLoadingWithMessage(`uploading file : ${percentCompleted}%`);
        },
      });
    },
    removeFile(index) {
      const key = this.files[index].key;
      if (!Number.isInteger(key)) {
        this.deletedFiles.push(key);
      }
      this.files.splice(index, 1);
      this.reOrderFiles();
    },
    reOrderFiles() {
      this.files = Object.assign([], this.files.map((f, idx) => ({ ...f, id: idx, no: idx + 1 })));
    },
    setEdit() {
      this.isEdit = true;
    },
    disableEdit() {
      this.isEdit = false;
    },
  },
  components: { Card, FormItem, Input, TextError, Button },
};
</script>

<style scoped>

</style>



// WEBPACK FOOTER //
// src/views/AppSetting/AdsSettings.vue